import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MailWarning } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useMe } from "@/lib/queries";

/**
 * "Confirm your email" — a banner, not a wall.
 *
 * Verification deliberately does not block signing in or using the dashboard.
 * Someone who has just signed up and cannot get in because a mail is slow, or
 * landed in spam, is someone who leaves and does not come back. The gate that
 * matters goes on publishing, where a wrong address means we cannot reach
 * them about a post that failed.
 *
 * Dismissible for the session only, not stored. A banner that can be dismissed
 * forever is a banner that stops working, and this one has something it wants
 * the person to actually do.
 */
export function VerifyEmailBanner() {
  const { t } = useTranslation();
  const me = useMe();
  const [dismissed, setDismissed] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");

  const user = me.data;
  if (!user || user.emailVerifiedAt || dismissed) return null;

  async function resend() {
    setState("sending");
    try {
      const result = await unwrap<{ sent: boolean }>(api.post("/auth/verify-email/resend", {}));
      setState(result.sent ? "sent" : "failed");
    } catch {
      setState("failed");
    }
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm">
      <MailWarning className="h-4 w-4 shrink-0 text-warning" aria-hidden />
      <span className="flex-1 text-muted">
        {state === "sent"
          ? t("auth.verifyResent", { email: user.email })
          : t("auth.verifyBanner", { email: user.email })}
      </span>

      {state !== "sent" ? (
        <button
          onClick={resend}
          disabled={state === "sending"}
          className="font-medium text-warning hover:underline disabled:opacity-50"
        >
          {state === "sending" ? t("auth.sending") : t("auth.resend")}
        </button>
      ) : null}

      {state === "failed" ? <span className="text-danger">{t("auth.resendFailed")}</span> : null}

      <button
        onClick={() => setDismissed(true)}
        className="text-muted hover:text-ink"
        aria-label={t("common.dismiss")}
      >
        ×
      </button>
    </div>
  );
}
